import { useEffect, type FC, useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Image, Layout, Spin, Grid, Popconfirm } from 'antd';
import { datadogRum } from '@datadog/browser-rum';
import { useAuth0 } from '@auth0/auth0-react';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { Text } from '@g2u/ui-kit';
import cx from 'classnames';

import { Menu, Header, ScrollTop, Switchvox, PopconfirmDescription, LostJobModal } from 'components';
import Logo from 'assets/images/geeks2u-logo-wide.svg';
import { BookingStore, RootStore } from 'store';
import { Toast, parseErrors } from 'helpers';
import { getCheckLostJobs } from 'queries';
import { JsonDataItem } from 'types';
import packageJson from '../../../package.json';
import styles from './default-page.module.scss';

type Props = Readonly<{ isLoading?: boolean }>;

export const DefaultPage: FC<Props> = observer((props) => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState<boolean>(true);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const { setBreadcrumbs, token, user, setToken } = RootStore;
  const { step, resetBookingProgress, lostJob, setLostJob } = BookingStore;
  const { Sider, Footer, Content } = Layout;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();

  useEffect(() => {
    setBreadcrumbs(null);
    setLoading(!!props.isLoading);
  }, []);

  useEffect(() => {
    // Datadog setup
    if (user && process.env.NODE_ENV !== 'development') {
      datadogRum.setUser({ id: user.ID, name: user.name });
    }
  }, [user]);

  useEffect(() => {
    const getToken = async () => {
      try {
        if (isAuthenticated) {
          const accessToken = await getAccessTokenSilently();
          setToken(accessToken);
        }
      } catch (error) {
        console.error('Token renewal failed, redirecting to login', error);
        void loginWithRedirect();
      }
    };

    void getToken();
  }, []);

  const { mutate: checkLostJob } = useMutation(getCheckLostJobs, {
    onSuccess: (data) => {
      setLostJob(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  useEffect(() => {
    token && step === 0 && checkLostJob();
  }, [step, token]);

  if (!isAuthenticated || !token) {
    return null;
  }

  return (
    <Layout className={styles.defaultPage} hasSider>
      <Sider
        width={screens.lg ? 264 : 200}
        collapsible
        collapsed={collapsed}
        className={styles.defaultPage__sidebar}
        onCollapse={(value) => {
          setCollapsed(value);
        }}
      >
        {step && step < 4 ? (
          <Popconfirm
            title="Alert"
            onConfirm={() => {
              navigate('/dashboard');
              resetBookingProgress();
            }}
            description={<PopconfirmDescription />}
            okText="Yes"
          >
            <div className={styles.defaultPage__sidebarLogo}>
              <Image src={Logo} alt="Geeks2U logo" preview={false} />
            </div>
          </Popconfirm>
        ) : (
          <Link
            to={'/'}
            className={styles.defaultPage__sidebarLogo}
            onClick={() => {
              resetBookingProgress();
            }}
          >
            <Image src={Logo} alt="Geeks2U logo" preview={false} />
          </Link>
        )}
        <Menu isCollapsed={collapsed} />
      </Sider>
      <Switchvox />
      <Layout className={cx(styles.defaultPage__main, collapsed && styles.defaultPage__main_collapsed)}>
        <Header />
        <Content className={styles.defaultPage__content}>
          {loading ? <Spin size="large" fullscreen /> : <Outlet />}
        </Content>
        <Footer className={styles.defaultPage__footer}>
          <Text.$ size={Text.Size.SM} type="secondary">
            v{packageJson.version}
          </Text.$>
        </Footer>
      </Layout>

      <LostJobModal
        isOpen={lostJob?.forceLostJob}
        onOk={() => {
          setLostJob(null);
        }}
      />

      <ScrollTop />
    </Layout>
  );
});
